import { useFormikContext, type FormikHelpers } from 'formik'
import * as React from 'react'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { EmailIcon } from 'src/assets/icons/customIcons/Email'
import { errorMessage } from 'src/helpers/fns'
import { useAdminAccess } from 'src/helpers/hooks'
import { type AuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { useTheme } from 'src/state/providers/Theme'
import { Button } from 'src/tailwind/components/Button'
import { Modal } from 'src/tailwind/components/Modal'
import * as Table from 'src/tailwind/components/Table'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import TextareaInput from 'src/views/components/forms/formik/TextareaInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import ScoreField from 'src/views/components/forms/ScoreField'
import NoContent from 'src/views/components/NoContent'
import MessageForm from '../../../../lecturer/courses/scores/MessageForm'
import { CRITERIA_TYPE, REMEDIATION_ELIGIBLE_ABOVE } from './constants'
import CriteriaDeadline from './CriteriaDeadline'
import { scoreError } from './validation'

interface ScoresFormValues {
  /**
   * `scores[studId][criteriaId]`. The API spec says `userId`, but the backend matches students by
   * `studId` — and a `userId` key passes validation, then silently zeroes the whole group.
   */
  readonly scores: Readonly<Record<string, Readonly<Record<string, string>>>>
  readonly card: { readonly title: string; readonly text: string }
}

interface MessageValues {
  readonly subject: string
  readonly body: string
}

const MESSAGE_INITIAL_VALUES: MessageValues = { subject: '', body: '' }

interface Props {
  readonly headers: AuthenticatedHeaders
  readonly courseId: string
  readonly groupId: number
  readonly semId: number
  readonly groupName: string
  /** A closed semester needs a service memo to justify any score change; the active one does not. */
  readonly isClosedSemester: boolean
  readonly data: Api.AdministratorCourseGroupScores
  readonly cards: readonly Api.AdministratorPaperCard[]
  readonly onSaved: () => Promise<void>
}

export default function StudentsScoresTable({
  headers,
  courseId,
  groupId,
  semId,
  groupName,
  isClosedSemester,
  data,
  cards,
  onSaved,
}: Props): React.ReactElement {
  const t = useTranslatable()
  const canManageScores = useAdminAccess().canManage('courseScores')
  const criterias = React.useMemo(() => data.paper?.criterias ?? [], [data.paper])
  const [recipients, setRecipients] = React.useState<readonly number[]>([])
  const [isMessageOpen, setIsMessageOpen] = React.useState(false)

  const toggleRecipient = React.useCallback((userId: number) => {
    setRecipients((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]))
  }, [])

  const toggleAllRecipients = React.useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      setRecipients(target.checked ? data.students.map((student) => student.userId) : [])
    },
    [data.students]
  )

  const openMessage = React.useCallback(() => setIsMessageOpen(true), [])
  const closeMessage = React.useCallback(() => setIsMessageOpen(false), [])

  // Recipients are the same numeric user ids the scores payload carries — legacy composes to them too.
  const handleSendMessage = React.useCallback(
    async (values: MessageValues, helpers: FormikHelpers<MessageValues>) => {
      try {
        await Api.postMessages({
          headers,
          body: { recipients: recipients.map(String), subject: values.subject, body: values.body },
        })
        toast.success(t('uncategorized:message_successfully_sent'))
        setIsMessageOpen(false)
        setRecipients([])
        helpers.resetForm()
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
        throw error
      }
    },
    [headers, recipients, t]
  )

  const initialValues = React.useMemo<ScoresFormValues>(() => {
    const scores: Record<string, Record<string, string>> = {}
    for (const student of data.students) {
      const row: Record<string, string> = {}
      for (const criteria of criterias) {
        const score = student.scores[String(criteria.id)]
        // Two decimals, the way the lecturer page and ScoreField render a stored score.
        row[String(criteria.id)] = score == null ? '' : score.toFixed(2)
      }
      scores[String(student.studId)] = row
    }
    return { scores, card: { title: '', text: '' } }
  }, [criterias, data.students])

  /**
   * `ScoreField` seeds its own input state once, so the cells have to remount when the saved
   * scores come back from the server; the totals change on every real save.
   */
  const dataRevision = React.useMemo(
    () => data.students.reduce((sum, student) => sum + student.finalScore + student.credits, 0),
    [data.students]
  )

  const handleSubmit = React.useCallback(
    async (values: ScoresFormValues, helpers: FormikHelpers<ScoresFormValues>) => {
      const rangeErrors: Record<string, string[]> = {}
      const scores: Record<string, Record<string, number>> = {}
      for (const [studId, row] of Object.entries(values.scores)) {
        const parsedRow: Record<string, number> = {}
        for (const [criteriaId, raw] of Object.entries(row)) {
          const max = criterias.find((criteria) => String(criteria.id) === criteriaId)?.max
          const error = max == null ? null : scoreError(raw, max, t)
          if (error != null) {
            rangeErrors[`scores.${studId}.${criteriaId}`] = [error]
          }
          // An empty cell and a 0 are the same thing to the backend, which is what legacy stores too.
          parsedRow[criteriaId] = raw.trim() === '' ? 0 : Number(raw)
        }
        scores[studId] = parsedRow
      }
      if (Object.keys(rangeErrors).length > 0) {
        throw { type: 'ErrorsObject', errors: rangeErrors }
      }

      const title = values.card.title.trim()
      const text = values.card.text.trim()
      const card = isClosedSemester && (title !== '' || text !== '') ? { title, text } : undefined

      try {
        await Api.putAdministratorCourseGroupScores({
          headers,
          args: { id: courseId, groupId },
          body: { semId, scores, ...(card != null && { card }) },
        })
        await onSaved()
        helpers.resetForm()
        toast.success(t('course:scores_saved_successfully'))
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
        throw error
      }
    },
    [courseId, criterias, groupId, headers, isClosedSemester, onSaved, semId, t]
  )

  if (data.students.length === 0) {
    return <NoContent header={t('common:records_not_found')} image={Warning} marginTop="5" />
  }

  return (
    <>
      <Form key={dataRevision} initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize isConfirmable>
        <FormError />
        {isClosedSemester && data.paper != null && <ServiceMemoFieldset cards={cards} />}
        <Table.Table className="mt-3">
          <Table.Thead>
            <Table.Tr>
              <Table.Th colSpan={criterias.length + 5} className="pb-0">
                <h3>{groupName}</h3>
              </Table.Th>
            </Table.Tr>
            <Table.Tr>
              <Table.Th style={{ width: '1%' }}>#</Table.Th>
              <Table.Th style={{ width: '1%' }}>
                <input
                  type="checkbox"
                  checked={recipients.length > 0 && recipients.length === data.students.length}
                  onChange={toggleAllRecipients}
                />
              </Table.Th>
              <Table.Th scope="col" className="pl-1 text-start">
                {t('uncategorized:name_surname')}
              </Table.Th>
              {criterias.map((criteria) => (
                <Table.Th scope="col" className="!text-center" style={{ fontSize: '14px' }} key={criteria.id}>
                  <div className="flex flex-col" title={criteria.name}>
                    <span>{criteria.acr}</span> <small style={{ fontSize: '12px' }}>(max-{criteria.max})</small>
                    <CriteriaDeadline deadline={criteria.deadline} />
                  </div>
                </Table.Th>
              ))}
              <Table.Th scope="col" className="!text-center">
                {t('common:total')}
              </Table.Th>
              <Table.Th scope="col" className="!text-center">
                {t('course:cred')}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.students.map((student, index) => (
              <StudentScoreRow
                key={student.studId}
                student={student}
                index={index}
                criterias={criterias}
                isRecipient={recipients.includes(student.userId)}
                onToggleRecipient={toggleRecipient}
              />
            ))}
          </Table.Tbody>
        </Table.Table>
        <div className="my-3 flex w-full justify-end gap-2">
          <Button variant="blue" type="button" disabled={recipients.length < 1} onClick={openMessage}>
            {t('message:send_message')}
          </Button>
          {/* Without a paper there is nothing to save — the API rejects an empty scores map. */}
          {data.paper != null && canManageScores && (
            <Button variant="blue" type="submit">
              {t('course:save_scores')}
            </Button>
          )}
        </div>
      </Form>
      <Modal
        title={t('message:messages_compose')}
        icon={<EmailIcon />}
        isModalOpen={isMessageOpen}
        onClose={closeMessage}
      >
        {isMessageOpen && (
          <Form
            initialValues={MESSAGE_INITIAL_VALUES}
            onSubmit={handleSendMessage}
            classNames={{ form: 'overflow-auto py-4' }}
            isConfirmable
          >
            <MessageForm>
              {data.students
                .filter((student) => recipients.includes(student.userId))
                .map((student, key) => (
                  <div className="mr-2" key={student.userId}>
                    {student.name}
                    {key === recipients.length - 1 ? '' : ','}
                  </div>
                ))}
            </MessageForm>
          </Form>
        )}
      </Modal>
    </>
  )
}

/**
 * Legacy tints the exam cells by the student's course outcome, not by each cell's own value
 * (probed on a live paper — see the scores-paper memory):
 * - final evaluation: green when credits were awarded, red when not;
 * - remediation exam: blank on a pass; on a fail, green when the total still leaves the student
 *   eligible to sit it (strictly above 41 — exactly 41 renders red), red otherwise;
 * - intermediate criteria: never tinted.
 */
function examCellClassName(criteriaType: number, student: Api.AdministratorPaperScoreStudent): string {
  const hasCredits = student.credits > 0
  if (criteriaType === CRITERIA_TYPE.FINAL_EVALUATION) {
    return hasCredits ? 'bg-primaryGreen/20' : 'bg-primaryRed/20'
  }
  if (criteriaType === CRITERIA_TYPE.REMEDIATION_EXAM && !hasCredits) {
    return student.finalScore > REMEDIATION_ELIGIBLE_ABOVE ? 'bg-primaryGreen/20' : 'bg-primaryRed/20'
  }
  return ''
}

function StudentScoreRow({
  student,
  index,
  criterias,
  isRecipient,
  onToggleRecipient,
}: {
  readonly student: Api.AdministratorPaperScoreStudent
  readonly index: number
  readonly criterias: readonly Api.AdministratorPaperCriteria[]
  readonly isRecipient: boolean
  readonly onToggleRecipient: (userId: number) => void
}): React.ReactElement {
  const t = useTranslatable()
  const canManageScores = useAdminAccess().canManage('courseScores')
  const theme = useTheme()
  const formik = useFormikContext<ScoresFormValues>()
  const hasCredits = student.credits > 0
  const tooltipId = `student-outcome-${student.studId}`
  const handleToggle = React.useCallback(() => onToggleRecipient(student.userId), [onToggleRecipient, student.userId])

  return (
    <Table.Tr>
      <Table.Td>{index + 1}</Table.Td>
      <Table.Td className="align-middle">
        <input type="checkbox" checked={isRecipient} onChange={handleToggle} />
      </Table.Td>
      <Table.Td className="pl-1 pr-4">
        <div className="flex flex-col" style={{ fontSize: '14px' }}>
          <span className="whitespace-nowrap text-primaryTextColor">{student.name}</span>
          <span className="whitespace-nowrap text-captionColor" style={{ fontSize: '12px' }}>
            ({student.persNum})
          </span>
        </div>
      </Table.Td>
      {criterias.map((criteria) => (
        <Table.Td key={criteria.id} className={examCellClassName(criteria.type, student)}>
          <div style={{ width: '70px', margin: 'auto' }}>
            <ScoreField
              name={`scores.${student.studId}.${criteria.id}`}
              loading={formik.isSubmitting}
              disabled={!canManageScores}
            />
          </div>
        </Table.Td>
      ))}
      <Table.Td className="text-center">{student.finalScore.toFixed(2)}</Table.Td>
      <Table.Td className="text-center">
        <Tooltip id={tooltipId} variant={theme === 'dark' ? 'dark' : 'light'}>
          {hasCredits ? t('course:student_earned_credits') : t('course:student_earned_no_credits')}
        </Tooltip>
        <span data-tooltip-id={tooltipId} className="cursor-help">
          {student.credits}
        </span>
      </Table.Td>
    </Table.Tr>
  )
}

function ServiceMemoFieldset({ cards }: { readonly cards: readonly Api.AdministratorPaperCard[] }): React.ReactElement {
  const t = useTranslatable()
  const formik = useFormikContext<ScoresFormValues>()

  return (
    <fieldset disabled={formik.isSubmitting} className="mt-3">
      <legend className="text-primaryTextColor" style={{ fontSize: '18px' }}>
        {t('course:service_memo')} [{t('course:service_memo_history')}: {cards.length}]
      </legend>
      <p className="mb-3 text-caption text-captionColor">{t('course:service_memo_required')}</p>
      <TextInput required name="card.title" type="text" placeholder={t('common:title')} label={t('common:title')} />
      <TextareaInput name="card.text" placeholder={t('common:text')} label={t('common:text')} rows={4} />
      {cards.length > 0 && (
        <ul className="mb-3 divide-y divide-borderGrey rounded-lg border border-borderGrey bg-primaryWhite">
          {cards.map((card) => (
            <li key={card.id} className="px-3 py-2.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-semibold text-primaryTextColor">{card.title}</span>
                {card.date != null && <span className="text-caption text-captionColor">{card.date}</span>}
              </div>
              {card.text != null && card.text !== '' && (
                <p className="mt-1 whitespace-pre-line text-caption text-primaryTextColor">{card.text}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  )
}
